// frontend/src/utils/drawer.js
import React from 'react';
// import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Box, Typography, Paper, Drawer } from '@barracuda-internal/bds-core';
import { getSeverityText } from './color';
import { SmallSeverityBars } from './utility';
import { JsonViewer } from "@textea/json-viewer";

import { Chip } from '@barracuda-internal/bds-core';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';


// Shared DrawerDetails component used by Vulnerabilities and SecureConfiguration
export default function DrawerDetails({ open, selected, onClose, url, isSafe }) {
  const [showJson, setShowJson] = React.useState(false);
  React.useEffect(() => {
    setShowJson(false);
  }, [selected]);
  return (
    <Drawer anchor="right" open={!!open} onClose={onClose}>
      <Box sx={{ width: 520, p: 3 }} role="presentation">
        {selected ? (
          <>
            {/* Title and Close Button */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{selected.title || selected.name}</Typography>
              <IconButton aria-label="close" onClick={onClose} sx={{ color: '#d1d3d5ff' }}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, position: 'relative' }}>
              <Box>
                <Typography color="text.secondary" sx={{ fontWeight: 500, fontSize: '1rem' }}>
                  Severity: {getSeverityText(selected.severity_level)}
                </Typography>
                <SmallSeverityBars severity={selected.severity_level} />
              </Box>
              <Box
                sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1.2,
            py: 0.4,
            borderRadius: '10px',
            background: isSafe
                    ? 'linear-gradient(90deg, #e8f5e9 60%, #b2dfdb 100%)'
                    : 'linear-gradient(90deg, #ffebee 60%, #ffe082 100%)',
                  color: isSafe ? '#2e7d32' : '#d32f2f',
            fontWeight: 600,
            fontSize: '0.8rem',
            gap: 0.5,
            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
            border: '1px solid rgba(46,125,50,0.15)',
          }}
              >
                {isSafe ? (
                  <ShieldOutlinedIcon sx={{ color: '#2e7d32', fontSize: 20, mr: 0.5 }} />
                ) : (
                  <ReportProblemRoundedIcon sx={{ color: '#d32f2f', fontSize: 20, mr: 0.5 }} />
                )}
                {isSafe ? 'Safe' : 'Risk'}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, position: 'relative' }}>
              <Box>
                <Typography color="text.secondary" sx={{ fontWeight: 500, fontSize: '1rem' }}>
                  Possible Attack: {selected.attack_type_title || 'N/A'}
                </Typography>
              </Box>
            </Box>

            {/* Assessment Performed Section */}
            <Paper sx={{ bgcolor: '#f7fafd', p: 3, mb: 3, boxShadow: 1, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Assessment Performed</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Parameters Checked: {selected.param}</Typography>
                  <Typography></Typography>
                  {/* You can add more details here if available */}
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Check Performed</Typography>
                  <Typography dangerouslySetInnerHTML={{__html: selected.assessment_details}}></Typography>
                </Box>
              </Box>
            </Paper>

            {/* Assessment Outcome Section */}
            <Paper sx={{ bgcolor: '#f7fafd', p: 3, mb: 3, boxShadow: 1, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Assessment Outcome</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography sx={{ fontWeight: 500, color: isSafe ? '#2e7d32' : '#d32f2f' }}>
                    {isSafe ? "Configuration is safe" : "Configuration is vulnerable for exploit"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Why is this considered {isSafe ? "safe" : "vulnerable"}?</Typography>
                  <Typography component="span" dangerouslySetInnerHTML={{ __html: isSafe ? selected.safe_config_description : selected.vulnerability_description }}></Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Risk of Exploit</Typography>
                  <Typography>{selected.attack_description}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Mitigation</Typography>
                  <Typography>{selected.mitigation}</Typography>
                </Box>
                {!isSafe && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Threat Category</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                      {(() => {
                        const cweVal = selected.cwe;
                        if (!cweVal && selected.cwe_id == null) return null;
                        const rawItems = cweVal ?? (selected.cwe_id ? [selected.cwe_id] : []);
                        const items = Array.isArray(rawItems) ? rawItems : [rawItems];
                        return items.map((it, idx) => {
                          let label = String(it);
                          let num = null;
                          if (typeof it === 'number' || /[0-9]+$/.test(String(it))) {
                            num = String(it).match(/(\d+)/)[0];
                            label = `CWE-${num}`;
                          } else if (typeof it === 'object' && it !== null) {
                            if (it.id || it.cwe) {
                              num = String(it.id || it.cwe);
                              label = it.name ? `CWE-${num}: ${it.name}` : `CWE-${num}`;
                            } else {
                              label = JSON.stringify(it);
                            }
                          } else {
                            const m = String(it).match(/CWE-(\d+)/i);
                            if (m) num = m[1];
                          }
                          const href = num ? `https://cwe.mitre.org/data/definitions/${num}.html` : null;
                          return href ? (
                            <Paper
                              component="a"
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              key={idx}
                              sx={{
                                p: 2,
                                bgcolor: '#ffffff',
                                borderRadius: 2,
                                minWidth: 220,
                                textDecoration: 'none',
                                color: 'inherit',
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'none' },
                              }}
                            >
                              <Typography sx={{ fontWeight: 600, fontSize: '1.10rem', mb: 2.5, }}>CWE</Typography>
                              <Box sx={{ mt: 1 }}>
                                <Typography sx={{ fontSize: '1rem' }}>{label}</Typography>
                              </Box>
                            </Paper>
                          ) : (
                            <Paper key={idx} sx={{ p: 2, bgcolor: '#ffffff', borderRadius: 2, minWidth: 220 }}>
                              <Typography sx={{ fontWeight: 600, fontSize: '1.25rem' }}>CWE</Typography>
                              <Box sx={{ mt: 1 }}>
                                <Typography sx={{ fontSize: '1rem' }}>{label}</Typography>
                              </Box>
                            </Paper>
                          );
                        });
                      })()}
                    </Box>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* JSON Details Toggle Section */}
            <Paper sx={{ bgcolor: '#f7fafd', p: 3, boxShadow: 0, borderRadius: 3, position: 'relative' }}>
              <Button
                bdsType="interactiveSubtle"
                sx={{ mb: 2, textTransform: 'none', fontSize: '0.95em', position: 'absolute', top: 0, right: 0, zIndex: 2 }}
                onClick={() => setShowJson((prev) => !prev)}
              >
                {showJson ? 'Show less' : 'Show more'}
              </Button>
              {showJson && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="subtitle2">Technical Details</Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 4, flexWrap: 'wrap' }}>
                    <Paper sx={{ bgcolor: '#ffffff', p: 1, boxShadow: 'none', border: 'none' }}>
                      {selected.details ? (
                        <JsonViewer value={selected.details} />
                      ) : (
                        <Typography color="text.secondary">No details available.</Typography>
                      )}
                    </Paper>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Scan Details</Typography>
                    <Typography>{selected.description}</Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          </>
        ) : null}
      </Box>
    </Drawer>
  );
}
